import React, { useState } from 'react';
import { Database, AlertTriangle, CheckCircle, Users, Briefcase, RefreshCw } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
import apiClient from '../services/apiClient';

interface SeedResponse {
  success: boolean;
  message?: string;
  clientsCreated?: number;
  mattersCreated?: number;
}

const SampleDataSeeding: React.FC = () => {
  const [isSeeding, setIsSeeding] = useState(false);
  const [currentData, setCurrentData] = useState<{ clients: number; matters: number } | null>(null);
  const [lastSeedResult, setLastSeedResult] = useState<SeedResponse | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { success: showSuccess, error: showError } = useToast();

  // Check current data count
  const checkCurrentData = async () => {
    setIsLoadingData(true);
    try {
      const [clients, matters] = await Promise.all([
        apiClient.getClients(),
        apiClient.getMatters()
      ]);

      setCurrentData({
        clients: clients.length || 0,
        matters: matters.length || 0
      });
    } catch (error) {
      console.error('Error fetching current data:', error);
      showError('Failed to fetch current data');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Seed sample data
  const seedSampleData = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will DELETE ALL existing clients and matters data and replace it with sample data. This action cannot be undone. Are you sure you want to continue?'
    );

    if (!confirmed) return;

    setIsSeeding(true);
    setLastSeedResult(null);

    try {
      const result = await apiClient.seedSampleData();

      if (result.success) {
        setLastSeedResult(result);
        showSuccess(`Successfully created ${result.clientsCreated} clients and ${result.mattersCreated} matters`);
        // Refresh current data after seeding
        setTimeout(() => checkCurrentData(), 1000);
      } else {
        throw new Error(result.message || 'Seeding failed');
      }
    } catch (error) {
      console.error('Seeding error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showError(`Seeding failed: ${errorMessage}`);
      setLastSeedResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setIsSeeding(false);
    }
  };

  // Load current data on component mount
  React.useEffect(() => {
    checkCurrentData();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center space-x-3 mb-4">
          <Database className="w-6 h-6 text-blue-400" />
          <h1 className="text-2xl font-bold text-white">Sample Data Seeding</h1>
        </div>
        <p className="text-gray-300">
          Populate your development environment with realistic sample data for testing and demonstration purposes.
        </p>
      </div>

      {/* Warning Notice */}
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-red-300 font-medium mb-2">⚠️ Important Warning</h3>
            <p className="text-red-200 text-sm">
              The seeding operation will <strong>DELETE ALL existing data</strong> from the clients and matters tables 
              before creating new sample data. This action cannot be undone. Only use this in development environments.
            </p>
          </div>
        </div>
      </div>

      {/* Current Data Status */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Current Database Status</h2>
          <button
            onClick={checkCurrentData}
            disabled={isLoadingData}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-md transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoadingData ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {currentData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Users className="w-6 h-6 text-blue-400" />
                <div>
                  <p className="text-sm text-gray-300">Total Clients</p>
                  <p className="text-2xl font-bold text-white">{currentData.clients}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Briefcase className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-sm text-gray-300">Total Matters</p>
                  <p className="text-2xl font-bold text-white">{currentData.matters}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading current data...</p>
          </div>
        )}
      </div>

      {/* Sample Data Details */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-4">Sample Data Preview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-green-400 font-medium mb-3">Clients (25 total)</h3>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• 15 Corporate clients (Acme Corp, Global Tech Solutions, etc.)</li>
              <li>• 10 Individual clients (Elizabeth Harrison, James Patterson, etc.)</li>
              <li>• Complete contact information and business addresses</li>
              <li>• Mix of active, pending, and inactive statuses</li>
              <li>• Realistic client IDs (CL0001, CL0002, etc.)</li>
            </ul>
          </div>
          <div>
            <h3 className="text-blue-400 font-medium mb-3">Matters (~60-75 total)</h3>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• 2-3 matters per client</li>
              <li>• Realistic legal practice areas (Corporate, Litigation, IP, etc.)</li>
              <li>• Professional matter titles ("Smith vs Johnson", "In re Estate", etc.)</li>
              <li>• Various statuses (Active 50%, Completed 30%, On-hold 15%, Cancelled 5%)</li>
              <li>• Billing amounts from $500 - $25,500</li>
              <li>• Recent activity dates within last 6 months</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Seeding Action */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-4">Seed Sample Data</h2>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-300 mb-2">
              Replace all existing data with realistic sample data for development and testing.
            </p>
            <p className="text-sm text-gray-400">
              This operation typically takes 5-10 seconds to complete.
            </p>
          </div>
          
          <button
            onClick={seedSampleData}
            disabled={isSeeding}
            className="flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white rounded-lg transition-colors font-medium"
          >
            {isSeeding ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Seeding Data...</span>
              </>
            ) : (
              <>
                <Database className="w-5 h-5" />
                <span>Seed Sample Data</span>
              </>
            )}
          </button>
        </div>

        {/* Last Operation Result */}
        {lastSeedResult && (
          <div className={`mt-4 p-4 rounded-lg border ${
            lastSeedResult.success 
              ? 'bg-green-900/20 border-green-700' 
              : 'bg-red-900/20 border-red-700'
          }`}>
            <div className="flex items-start space-x-3">
              {lastSeedResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
              )}
              <div>
                <h4 className={`font-medium ${
                  lastSeedResult.success ? 'text-green-300' : 'text-red-300'
                }`}>
                  {lastSeedResult.success ? 'Seeding Successful!' : 'Seeding Failed'}
                </h4>
                <p className={`text-sm ${
                  lastSeedResult.success ? 'text-green-200' : 'text-red-200'
                }`}>
                  {lastSeedResult.success && lastSeedResult.clientsCreated && lastSeedResult.mattersCreated
                    ? `Created ${lastSeedResult.clientsCreated} clients and ${lastSeedResult.mattersCreated} matters`
                    : lastSeedResult.message || 'An error occurred during the seeding process'
                  }
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Next Steps */}
      <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
        <h3 className="text-blue-300 font-medium mb-2">After Seeding</h3>
        <ul className="text-blue-200 text-sm space-y-1">
          <li>• Navigate to the Dashboard to see updated statistics</li>
          <li>• Visit the Clients page to browse the new sample clients</li>
          <li>• Explore client details to see linked matters and realistic data</li>
          <li>• Test search, filtering, and CRUD operations with the sample data</li>
        </ul>
      </div>
    </div>
  );
};

export default SampleDataSeeding;
